/**
 * MODALS — UI-методы для service-модала и optional-services modal flow.
 *
 * Phase 5 sub-extract (Sprint 15 C.3 part 1, 2026-05-10).
 * Извлечено из public/js/app.js (DOMAIN: SERVICE MODAL и
 * DOMAIN: OPTIONAL SERVICES).
 *
 * Содержит:
 *   • Service modal — show/hide/add (универсальный — service / hotel /
 *     flight / other / optional)
 *   • Optional services — модал + per-service render/edit/move/remove
 *
 * Подключение из entry.js через `attachModals(ProfessionalQuoteCalculator)`.
 *
 * @module app/modals
 */

'use strict';

const _t = (k, fb) => {
    const v = window.i18n?.t?.(k);
    return (v && v !== k) ? v : (fb || k);
};

export function attachModals(ClassRef) {
    Object.assign(ClassRef.prototype, {
        // ════════════════════════════════════════════════════════════════════
        // ▼ DOMAIN: SERVICE MODAL
        //   Универсальный модал — открывается для service / hotel / flight /
        //   other / optional. Тип берётся из state.currentServiceType.
        // ════════════════════════════════════════════════════════════════════

        showServiceModal() {
            // Если currentServiceType не указан, устанавливаем по умолчанию
            if (!this.state.currentServiceType || this.state.currentServiceType === 'service') {
                this.state.currentServiceType = 'service';
            }

            const modal = document.getElementById('service-modal');
            if (modal) {
                modal.classList.add('show');

                const type = this.state.currentServiceType;

                // Меняем заголовок
                const title = document.getElementById('service-modal-title');
                if (title) {
                    if (type === 'hotel') {
                        title.textContent = _t('app.modals.add_hotel', '🏨 Add hotel');
                    } else if (type === 'flight') {
                        title.textContent = _t('app.modals.add_flight', '✈️ Add flight');
                    } else if (type === 'other') {
                        title.textContent = _t('app.modals.add_other', '📋 Add other service');
                    } else if (type === 'optional') {
                        title.textContent = _t('app.modals.add_optional', 'Add optional service');
                    } else {
                        title.textContent = _t('app.modals.add_service', 'Add service');
                    }
                }

                // Показываем/скрываем поле "День"
                const dayRow = document.getElementById('service-day-row');
                if (dayRow) {
                    if (type === 'hotel' || type === 'flight' || type === 'other') {
                        dayRow.style.display = 'none';
                    } else {
                        dayRow.style.display = 'flex';
                    }
                }

                // Показываем/скрываем поле "Регион" (только для отеля)
                const regionRow = document.getElementById('service-region-row');
                const regionSelect = document.getElementById('service-region');
                if (regionRow && regionSelect) {
                    if (type === 'hotel') {
                        regionRow.style.display = 'flex';
                        // Заполняем регионы
                        regionSelect.innerHTML = this.regions.map(region =>
                            `<option value="${region}" ${region === this.currentRegion ? 'selected' : ''}>${region}</option>`
                        ).join('');
                    } else {
                        regionRow.style.display = 'none';
                    }
                }

                // Меняем подпись поля "Цена"
                const priceLabel = document.getElementById('service-price-label');
                if (priceLabel) {
                    if (type === 'hotel') {
                        priceLabel.textContent = _t('app.modals.price_per_night', '💵 Per night:');
                    } else if (type === 'flight') {
                        priceLabel.textContent = _t('app.modals.price_per_ticket', '💵 Per ticket:');
                    } else {
                        priceLabel.textContent = _t('app.modals.price_label', 'Price:');
                    }
                }

                // Focus on first input
                setTimeout(() => {
                    const nameInput = document.getElementById('service-name');
                    if (nameInput) nameInput.focus();
                }, 300);
            }
        },

        hideServiceModal() {
            const modal = document.getElementById('service-modal');
            if (modal) {
                modal.classList.remove('show');

                // Clear form
                ['service-name', 'service-description', 'service-contractor', 'service-day', 'service-price'].forEach(id => {
                    const element = document.getElementById(id);
                    if (element) {
                        element.value = '';
                        element.classList.remove('error');
                    }
                });
                const requiresPrepayment = document.getElementById('service-requires-prepayment');
                if (requiresPrepayment) requiresPrepayment.checked = false;
            }
        },

        addCustomService() {
            const nameElement = document.getElementById('service-name');
            const descriptionElement = document.getElementById('service-description');
            const contractorElement = document.getElementById('service-contractor');
            const dayElement = document.getElementById('service-day');
            const priceElement = document.getElementById('service-price');
            const regionElement = document.getElementById('service-region');

            if (!nameElement || !priceElement) return;

            const name = nameElement.value.trim();
            const description = descriptionElement ? descriptionElement.value.trim() : '';
            const contractor = contractorElement ? contractorElement.value.trim() : '';
            const price = Math.max(0, parseFloat(priceElement.value) || 0);

            // День нужен только для обычных услуг
            const needsDay = this.state.currentServiceType === 'service' || this.state.currentServiceType === 'optional';
            const day = needsDay ? Math.max(1, parseInt(dayElement?.value) || 1) : 1;

            // Регион: для отелей из модального окна, для остальных текущий регион
            const serviceType = this.state.currentServiceType;
            const region = (serviceType === 'hotel' && regionElement)
                ? regionElement.value
                : this.currentRegion;

            const requiresPrepaymentEl = document.getElementById('service-requires-prepayment');
            const requires_prepayment = requiresPrepaymentEl ? requiresPrepaymentEl.checked : false;

            const service = {
                id: Date.now().toString(),
                name: name,
                description: description || name,
                day: day,
                price: price,
                quantity: 1,
                markup: 0,
                contractor: contractor,
                excludeFromMarkup: false,
                region: region,
                requires_prepayment, // Sprint 19a: must be paid by client before we can book
                // Checklist fields
                done: false,        // Забронировано
                paid: false,        // Оплачено
                comment: ''         // Комментарий менеджера
            };

            const errors = this.validateService(service);
            if (errors.length > 0) {
                this.showNotification(errors.join(', '), true);
                return;
            }

            // Track global usage (для кастомных услуг без templateId, только общий счетчик)
            this.trackGlobalUsage(null, name, null);

            // Определяем куда добавлять в зависимости от типа (serviceType уже объявлен выше)
            if (serviceType === 'hotel') {
                this.state.hotels.unshift(service);
                this.renderHotels();
                this.showNotification(_t('app.modals.added_hotel', 'Hotel added'));
            } else if (serviceType === 'flight') {
                this.state.flights.unshift(service);
                this.renderFlights();
                this.showNotification(_t('app.modals.added_flight', 'Flight added'));
            } else if (serviceType === 'other') {
                this.state.otherServices.unshift(service);
                this.renderOtherServices();
                this.showNotification(_t('app.modals.added_other', 'Other service added'));
            } else if (this.isAddingOptional || serviceType === 'optional') {
                this.state.optionalServices.unshift(service);
                this.renderOptionalServices();
                this.showNotification(_t('app.modals.added_optional', 'Optional service added'));
                this.isAddingOptional = false;
            } else {
                // Обычная услуга
                this.state.services.unshift(service);
                this.renderServices();
                this.showNotification(_t('app.modals.added_service', 'Service added'));
            }

            this.scheduleUpdate();
            this.markQuoteAsUnsaved();
            this.hideServiceModal();

            // Сбрасываем тип после добавления
            this.state.currentServiceType = 'service';
        },

        // ════════════════════════════════════════════════════════════════════
        // ▼ DOMAIN: OPTIONAL SERVICES
        //   Услуги, которые остаются "за бортом" сметы — клиент может потом
        //   докупить, или менеджер перенесёт в основную смету.
        // ════════════════════════════════════════════════════════════════════

        showOptionalServiceModal() {
            this.isAddingOptional = true;
            this.showServiceModal();
        },

        addOptionalService(service) {
            this.state.optionalServices.unshift(service);
            this.renderOptionalServices();
            this.markQuoteAsUnsaved();
        },

        renderOptionalServices() {
            const container = document.getElementById('optional-services-list');
            if (!container) return;

            if (this.state.optionalServices.length === 0) {
                container.innerHTML = `<div class="text-xs text-muted" style="padding: calc(var(--spacing) * 4); text-align: center;">${_t('app.modals.no_optional', 'No optional services added')}</div>`;
                return;
            }

            container.innerHTML = this.state.optionalServices.map(service => {
                const activityDate = this.calculateActivityDate(service.day);
                const serviceTotal = this.calculateServiceTotal(service);
                const unitPrice = service.price * (1 + service.markup / 100);
                const totalPrice = unitPrice * service.quantity;

                return `
                    <div class="service-card" data-optional-service-id="${service.id}" style="border-color: #8b5cf6; background: rgb(139 92 246 / 0.02);">
                        <div class="service-header" style="background: rgb(139 92 246 / 0.05);">
                            <div class="service-header-left">
                                <div>
                                    <div style="font-size: 14px; font-weight: 600; color: var(--gray-900); line-height: 1.3;">${service.name}</div>
                                    <div style="font-size: 11px; color: var(--gray-500); margin-top: 1px;">${_t('app.services_list.day_prefix', 'Day')} ${service.day}${activityDate ? ' (' + activityDate + ')' : ''}</div>
                                </div>
                            </div>
                            <div style="display: flex; gap: calc(var(--spacing) * 1); align-items: center;">
                                <span style="font-size: 10px; color: #6b7280; white-space: nowrap;"><i data-lucide="map-pin" style="width: 9px; height: 9px; display: inline; vertical-align: text-top;"></i> ${service.region || this.currentRegion}</span>
                                <button class="btn btn-sm btn-primary" onclick="window.QuoteCalc.moveOptionalToMain('${service.id}')" title="${_t('app.modals.move_to_main_title', 'Move to main quote')}" style="font-size: 10px; padding: 4px 8px;">${_t('app.modals.move_to_main_label', '→ Move to main')}</button>
                                <button class="btn btn-sm remove-service-btn" onclick="window.QuoteCalc.removeOptionalService('${service.id}')" title="${_t('app.modals.remove_service_title', 'Remove service')}">×</button>
                            </div>
                        </div>
                        <div class="service-content">
                            <div class="form-row" style="margin-bottom: calc(var(--spacing) * 1); flex-wrap: wrap; row-gap: calc(var(--spacing) * 0.75);">
                                <div style="display: flex; align-items: center; gap: 6px;">
                                    <label style="font-size: 10px; color: var(--gray-500); white-space: nowrap;">${_t('app.services_list.label_day', 'Day:')}</label>
                                    <input type="number" class="input" value="${service.day}" min="1" style="width: 50px; font-size: 11px; padding: 4px 6px; text-align: center;" onchange="QuoteCalc.updateOptionalServiceField('${service.id}', 'day', parseInt(this.value) || 1)">
                                </div>

                                <div style="display: flex; align-items: center; gap: 6px;">
                                    <label style="font-size: 10px; color: var(--gray-500); white-space: nowrap;">${_t('app.services_list.label_price', 'Price:')}</label>
                                    <input type="number" class="input" value="${service.price}" min="0" style="width: 80px; font-size: 11px; padding: 4px 6px; text-align: center;" onchange="QuoteCalc.updateOptionalServiceField('${service.id}', 'price', parseFloat(this.value) || 0)">
                                </div>

                                <div style="display: flex; align-items: center; gap: 6px;">
                                    <label style="font-size: 10px; color: var(--gray-500); white-space: nowrap;">${_t('app.services_list.label_quantity', 'Qty:')}</label>
                                    <input type="number" class="input" value="${service.quantity}" min="1" style="width: 50px; font-size: 11px; padding: 4px 6px; text-align: center;" onchange="QuoteCalc.updateOptionalServiceField('${service.id}', 'quantity', parseInt(this.value) || 1)">
                                </div>

                                <div style="display: flex; align-items: center; gap: 6px;">
                                    <label style="font-size: 10px; color: var(--gray-500); white-space: nowrap;">${_t('app.services_list.label_markup', 'Markup:')}</label>
                                    <input type="number" class="input" value="${service.markup}" min="0" style="width: 50px; font-size: 11px; padding: 4px 6px; text-align: center;" onchange="QuoteCalc.updateOptionalServiceField('${service.id}', 'markup', parseFloat(this.value) || 0)">
                                    <span style="font-size: 10px; color: var(--gray-500);">%</span>
                                </div>
                            </div>

                            ${(this.getSupplierName ? this.getSupplierName(service) : service.contractor) ? `
                            <div class="form-row" style="align-items: flex-start; margin-bottom: calc(var(--spacing) * 1);">
                                <label style="padding-top: 3px; font-size: 10px; color: var(--gray-500); text-transform: uppercase; letter-spacing: 0.3px; width: 100px; flex-shrink: 0;">${_t('app.services_list.label_contractor', 'Supplier:')}</label>
                                <div style="flex: 1; min-width: 0; font-size: 12px; color: var(--gray-700); line-height: 1.5;">${this.getSupplierName ? this.getSupplierName(service) : service.contractor}</div>
                            </div>
                            ` : ''}

                            <div class="form-row" style="margin-bottom: 0; align-items: center; flex-wrap: wrap; row-gap: calc(var(--spacing) * 1);">
                                <div style="margin-left: auto; text-align: right; min-width: 80px;">
                                    <div style="font-size: 15px; font-weight: 600; color: var(--success); white-space: nowrap;">
                                        ${this.formatCurrency(totalPrice)}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                `;
            }).join('');
        },

        moveOptionalToMain(serviceId) {
            const service = this.state.optionalServices.find(s => s.id === serviceId);
            if (!service) return;

            // Remove from optional
            this.state.optionalServices = this.state.optionalServices.filter(s => s.id !== serviceId);

            // Add to main services
            this.state.services.unshift(service);

            this.renderServices();
            this.renderOptionalServices();
            this.scheduleUpdate();
            this.markQuoteAsUnsaved();
            this.showNotification(_t('app.modals.moved_to_main', 'Service moved to the main quote'));
        },

        updateOptionalServiceField(serviceId, field, value) {
            const service = this.state.optionalServices.find(s => s.id === serviceId);
            if (!service) return;

            service[field] = value;
            this.renderOptionalServices();
            this.markQuoteAsUnsaved();
        },

        async removeOptionalService(serviceId) {
            const { confirmModal } = await import('/public/js/components/text-prompt-modal.js');
            const ok = await confirmModal({
                title: _t('app.modals.remove_optional_confirm_title', 'Remove optional service?'),
                danger: true
            });
            if (!ok) return;

            this.state.optionalServices = this.state.optionalServices.filter(s => s.id !== serviceId);
            this.renderOptionalServices();
            this.markQuoteAsUnsaved();
            this.showNotification(_t('app.modals.removed_optional', 'Optional service removed'));
        }
    });
}
